import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, SlidersHorizontal, Target, UserPlus } from 'lucide-react';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Switch } from '@components/Switch';
import settingsService from '@services/settingsService';
import type { AppSettings } from '@/types/planning';
import { formatDateTimeBR } from '@/utils/formatters';

const TRIAL_MIN = 1;
const TRIAL_MAX = 365;

/**
 * Administração → Configurações (só admin): liberação geral do Acervo CAC; cadastro aberto (login
 * com Google cria a conta na hora) e dias de teste do cadastro novo (Fase 8).
 * Regra do CAC no backend: admin sempre tem; os demais têm se a liberação geral OU a individual estiver ligada.
 */
export const SettingsAdminView: React.FC = () => {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingCac, setSavingCac] = useState(false);
  const [savingSignup, setSavingSignup] = useState(false);
  const [savingTrial, setSavingTrial] = useState(false);
  const [trialDays, setTrialDays] = useState('');
  const [trialError, setTrialError] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const apply = (s: AppSettings) => {
    setSettings(s);
    setTrialDays(String(s.trial_days ?? ''));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      apply(await settingsService.get());
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleCac = async (value: boolean) => {
    setSavingCac(true);
    try {
      apply(await settingsService.updateSettings({ cac_enabled_for_all: value }));
    } catch (err) {
      setError(err);
    } finally {
      setSavingCac(false);
    }
  };

  const toggleSignup = async (value: boolean) => {
    setSavingSignup(true);
    try {
      apply(await settingsService.updateSettings({ signup_enabled: value }));
    } catch (err) {
      setError(err);
    } finally {
      setSavingSignup(false);
    }
  };

  const parsedTrial = Number(trialDays);
  const trialValid = /^\d+$/.test(trialDays.trim()) && parsedTrial >= TRIAL_MIN && parsedTrial <= TRIAL_MAX;
  const trialDirty = settings !== null && trialValid && parsedTrial !== settings.trial_days;

  const saveTrial = async () => {
    if (!trialValid) {
      setTrialError(`Informe um número inteiro entre ${TRIAL_MIN} e ${TRIAL_MAX}.`);
      return;
    }
    setTrialError(null);
    setSavingTrial(true);
    try {
      apply(await settingsService.updateSettings({ trial_days: parsedTrial }));
    } catch (err) {
      setError(err);
    } finally {
      setSavingTrial(false);
    }
  };

  const updatedInfo = settings?.updated_at ? (
    <>
      {' '}
      Última alteração em {formatDateTimeBR(settings.updated_at)}
      {settings.updated_by ? ` por ${settings.updated_by}` : ''}.
    </>
  ) : null;

  return (
    <div className="space-y-4 max-w-3xl">
      <h1 className="text-h3 flex items-center gap-2">
        <SlidersHorizontal size={20} className="text-slate-500" /> Configurações
      </h1>
      <p className="text-small">Ajustes globais da aplicação. Só administradores veem esta tela.</p>

      {loading && !settings ? (
        <p className="text-center text-slate-400 py-6">
          <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
        </p>
      ) : (
        <>
          {/* ───────── Cadastro e período de teste (Fase 8) ───────── */}
          <div className="card p-4 sm:p-5 space-y-4">
            <div className="flex items-start gap-3">
              <span className="p-2 rounded-lg bg-emerald-50 text-emerald-700 flex-shrink-0">
                <UserPlus size={18} />
              </span>
              <div className="min-w-0 flex-1 space-y-4">
                <div>
                  <h2 className="text-base font-semibold text-slate-800">Cadastro e período de teste</h2>
                  <p className="text-small mt-1">
                    Quem entra com a <strong>conta Microsoft</strong> precisa estar atribuído ao aplicativo no Entra ID. Quem entra com o{' '}
                    <strong>Google</strong> tem o cadastro criado na hora, como <em>Usuário</em>, no plano de <em>teste</em>, com acesso
                    até hoje + dias de teste. Depois disso, o plano e a data de término são ajustados em{' '}
                    <a href="/admin/users" className="text-emerald-700 hover:underline">
                      Usuários → Acesso
                    </a>
                    .
                  </p>
                </div>

                <Switch
                  checked={!!settings?.signup_enabled}
                  onChange={toggleSignup}
                  loading={savingSignup}
                  disabled={savingCac || savingTrial}
                  label={<span className="font-medium">Cadastro aberto (login com Google cria a conta na hora)</span>}
                />
                <p className="text-small">
                  {settings?.signup_enabled
                    ? 'Ligado: qualquer conta Google com e-mail verificado consegue criar um cadastro e começar o teste.'
                    : 'Desligado: quem ainda não tem cadastro vê "O cadastro de novos usuários está fechado no momento". Quem já se cadastrou continua entrando.'}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-[10rem_auto] items-start gap-2">
                  <Input
                    label="Dias de teste"
                    type="number"
                    inputMode="numeric"
                    min={TRIAL_MIN}
                    max={TRIAL_MAX}
                    step={1}
                    value={trialDays}
                    onChange={(e) => {
                      setTrialDays(e.target.value);
                      setTrialError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveTrial();
                    }}
                    disabled={savingTrial}
                    error={trialError ?? undefined}
                    helperText={`De ${TRIAL_MIN} a ${TRIAL_MAX}. Vale para os cadastros feitos a partir de agora; não muda quem já está em teste.`}
                  />
                  {/* Alinha o botão com o campo (abaixo do rótulo), não com o texto de ajuda. */}
                  <div className="sm:pt-6">
                    <Button variant="primary" onClick={saveTrial} loading={savingTrial} disabled={!trialDirty || savingSignup || savingCac} className="w-full sm:w-auto">
                      Salvar
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ───────── Acervo CAC ───────── */}
          <div className="card p-4 sm:p-5 space-y-4">
            <div className="flex items-start gap-3">
              <span className="p-2 rounded-lg bg-emerald-50 text-emerald-700 flex-shrink-0">
                <Target size={18} />
              </span>
              <div className="min-w-0 flex-1 space-y-3">
                <div>
                  <h2 className="text-base font-semibold text-slate-800">Acervo CAC</h2>
                  <p className="text-small mt-1">
                    O módulo Acervo CAC (armas, CR/CRAF/GT e vencimentos) só aparece para quem está liberado. O administrador sempre tem
                    acesso; os demais usuários têm quando a liberação geral abaixo está ligada
                    <strong> ou</strong> quando a liberação individual está marcada em{' '}
                    <a href="/admin/users" className="text-emerald-700 hover:underline">
                      Administração → Usuários
                    </a>
                    .
                  </p>
                </div>
                <Switch
                  checked={!!settings?.cac_enabled_for_all}
                  onChange={toggleCac}
                  loading={savingCac}
                  disabled={savingSignup || savingTrial}
                  label={<span className="font-medium">Liberar Acervo CAC para todos os usuários</span>}
                />
                <p className="text-small">
                  {settings?.cac_enabled_for_all
                    ? 'Ligado: todos os usuários ativos veem o Acervo CAC, independentemente da liberação individual.'
                    : 'Desligado: só o administrador e os usuários com liberação individual veem o Acervo CAC.'}
                </p>
              </div>
            </div>
          </div>

          {updatedInfo && <p className="text-small">{updatedInfo}</p>}
        </>
      )}

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
